import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classnames from 'classnames';
import { useTheme } from '@iq/react-components';

import VisualizationEditor from '../../VisualizationEditor';
import Visualization from '../../Visualization';
import Loader from '../../Loader';
import Pagination from '../../Pagination';
import Tabs, { Tab } from '../../Tabs/Tabs';
import VisualizationsHeader from './components/VisualizationsHeader';
import ExportTimeSeriesModal from '../../SiteAdminView/VariableView/components/ExportTimeSeriesModal';
import { getStarPlotVariables } from '../../Visualization/visualizations/timeSeries/StarVisualization/helpers';
import { SELECTABLE_VIZUALISATIONS } from '../../../constants';
import LabelWithTooltip from '../../LabelWithTooltip';

import { getHasPermission } from '../../../bundles/auth';
import {
  getVisualizations,
  resetVisualizations,
  LAST_VALUE_VISUALIZATIONS,
} from '../../../bundles/visualizations';
import { usePanelSubscription } from '../../../bundles/visualizations/hooks';
import { getTags, requestTags } from '../../../bundles/tags';
import {
  getStaticComponents,
  getComponentsUpdatedAt,
  getScopedComponents,
  getFilteredActiveComponentId,
} from '../../../bundles/components';
import {
  useDateSubscription,
  stopSitePolling,
  startSitePolling,
  getTimezone,
} from '../../../bundles/application';
import { SignalViewerContainer } from '../../SignalViewerContainer';
import { requestTfrTemplates } from '../../../bundles/tfr-templates';

const EXTRA_SMALL_VIZ_TYPES = new Set(['metric', 'status', 'state']);

function removeLastItemBorder() {
  const container = document.querySelector('.width-small');
  if (container) {
    const items = [...container.children];
    const columns = getComputedStyle(container).gridTemplateColumns.split(' ').length;
    // Remove each row last item border
    for (const [index, item] of items.entries()) {
      const newItem = item;
      newItem.style.borderRight = (index + 1) % columns === 0 ? 'none' : '1px solid #e6e6e6';
    }
    // Remove border from the last item in the entire grid
    if (items.length > 0) {
      items.at(-1).style.border = 'none';
    }
  }
}

const VisualizationsPanel = ({
  visualizations: includedVisualizations = [],
  visualizationTypes = [],
  visualizationLimit = 10,
  width = 'medium',
  height = 'medium',
  format = 'wide',
  syncVisualizationTooltip = false,
  componentScope,
  autoPopulate = false,
  hideToolbar = true,
  hiddenToolbarItems = [],
  isFullScreen = false,
  site,
  panelId,
  tabGroups = [],
  showTabs = true,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { org, id: siteId } = site || {};
  const timezone = useSelector(getTimezone);
  const tags = useSelector(getTags);
  const storedVisualizations = useSelector(getVisualizations);
  const visualizations = useMemo(
    () =>
      storedVisualizations?.toSorted((a, b) => {
        const { configuration: { priority: aPriority } = { priority: 'none' } } = a;
        const { configuration: { priority: bPriority } = { priority: 'none' } } = b;
        return /[A-Za-z]/.test(aPriority) - /[A-Za-z]/.test(bPriority) || aPriority - bPriority;
      }),
    [storedVisualizations]
  );
  const visualizationNames = visualizations.map((v) => v.name.toLowerCase());
  const activeComponentId = useSelector(getFilteredActiveComponentId);
  const components = useSelector(getStaticComponents);
  const componentsUpdatedAt = useSelector(getComponentsUpdatedAt);
  const canEditVisualizations = useSelector((state) =>
    getHasPermission(state, 'visualizations/Write', { org, site: siteId })
  );

  const canDeleteTemplate = useSelector((state) =>
    getHasPermission(state, 'tfr-templates/Delete', { org, site: siteId })
  );

  const pollingDateRange = useDateSubscription(panelId);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);
  const [filterValue, setFilterValue] = useState('');
  const [debouncedFilterValue, setDebouncedFilterValue] = useState('');
  const [signalViewer, setSignalViewer] = useState(false);
  const [allSelected, setAllSelected] = useState(false);
  const [selectedVisualizations, setSelectedVisualizations] = useState([]);
  const [showExportForm, setShowExportForm] = useState(false);
  const [exportGranularity, setExportGranularity] = useState('86400s'); // 1 day default
  const [activeTab, setActiveTab] = useState(() => {
    const hasTabGroups = tabGroups?.some((tg) => tg.tags?.length > 0);
    return hasTabGroups ? 0 : null;
  });
  const [tagFilter, setTagFilter] = useState([]);

  // Filter out deleted tags from tabGroups
  const validTabGroups = useMemo(() => {
    if (!tabGroups?.length) return [];

    // If tags are not available yet, show all tabs (don't filter)
    if (!tags || tags.length === 0) {
      // If tabs have any tags, show them as valid
      return tabGroups.filter((tabGroup) => tabGroup.tags?.length > 0);
    }

    const tagIds = new Set(tags.map((t) => t.id));
    return tabGroups
      .map((tabGroup) => ({
        ...tabGroup,
        tags: tabGroup.tags?.filter((tagId) => tagIds.has(tagId)) || [],
      }))
      .filter((tabGroup) => tabGroup.tags.length > 0);
  }, [tabGroups, tags]);

  // Initialize or update activeTab based on validTabGroups
  useEffect(() => {
    if (validTabGroups?.length > 0 && activeTab === null) {
      setActiveTab(0);
    } else if (activeTab !== null && validTabGroups?.length === 0) {
      setActiveTab(null);
    } else if (activeTab !== null && activeTab >= validTabGroups?.length) {
      setActiveTab(Math.max(0, validTabGroups.length - 1));
    }
  }, [validTabGroups, activeTab]);

  useEffect(() => {
    setExportGranularity(pollingDateRange.granularity);
  }, [pollingDateRange]);

  const scopedComponentIds = useMemo(
    () =>
      getScopedComponents({
        components,
        componentsUpdatedAt,
        activeComponentId,
        componentScope,
      }).map((c) => c.id),
    [componentsUpdatedAt, activeComponentId, componentScope, components]
  );

  useEffect(() => {
    dispatch(requestTfrTemplates({ site: site.id, templateType: 'signal' }));
  }, [site.id, dispatch]);

  // Ensure tags are loaded for tag filtering
  useEffect(() => {
    if (siteId && (!tags || tags.length === 0)) {
      dispatch(requestTags(siteId));
    }
  }, [siteId, tags, dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilterValue(filterValue);
    }, 500);

    return () => clearTimeout(timer);
  }, [filterValue]);

  useEffect(() => {
    if (signalViewer) dispatch(stopSitePolling());
  }, [signalViewer, dispatch]);

  const handleCloseExportModal = useCallback(() => setShowExportForm(false), [setShowExportForm]);

  const scopedVisualizations = useMemo(() => {
    let scopedViz = visualizations;
    if (
      autoPopulate &&
      !activeComponentId &&
      componentScope.selectedScope === 'all' &&
      visualizations
    ) {
      if (visualizationTypes.length > 0) {
        scopedViz = visualizations.filter((viz) => visualizationTypes.includes(viz.type));
      }
    } else if (autoPopulate && scopedComponentIds && visualizations) {
      scopedViz = visualizations.filter((viz) => {
        // if we've specifically included the visualization
        if (includedVisualizations.includes(viz.name)) {
          return true;
        }
        // otherwise ignore if we've specified types to show and it's not of type
        if (visualizationTypes.length > 0 && !visualizationTypes.includes(viz.type)) {
          return false;
        }
        // else check if it falls in component scope
        const compIds = (viz.relations || [])
          .filter((relation) => relation?.entity_type?.includes('component'))
          .map((relation) => relation.entity_id);
        let inScope = false;
        for (const id of compIds) {
          if (scopedComponentIds.includes(id)) {
            inScope = true;
          }
        }
        return inScope;
      });
    } else {
      scopedViz = includedVisualizations
        .map((name) => visualizations.find((v) => v.name === name))
        .filter(Boolean);
    }
    if (height === 'extra-small') {
      return scopedViz.filter((viz) => ['metric', 'status', 'state'].includes(viz?.type));
    }
    return scopedViz;
  }, [
    autoPopulate,
    includedVisualizations,
    activeComponentId,
    componentScope,
    scopedComponentIds,
    visualizations,
    visualizationTypes,
    height,
  ]);

  const shouldShowTabs = validTabGroups?.length >= 2 && showTabs;
  const tabFilteredVisualizations = useMemo(() => {
    if (shouldShowTabs && activeTab !== null) {
      const currentTab = validTabGroups[activeTab];
      if (currentTab?.tags?.length > 0) {
        const tabTags = currentTab.tags;
        return scopedVisualizations.filter((viz) => {
          const vizTagsArray = Array.isArray(viz.tags) ? viz.tags : [];
          return vizTagsArray.some((vizTag) => tabTags.includes(vizTag));
        });
      }
    }
    return scopedVisualizations;
  }, [scopedVisualizations, validTabGroups, activeTab, shouldShowTabs]);

  const tagFilteredVisualizations = useMemo(() => {
    if (!tagFilter || tagFilter.length === 0) {
      return tabFilteredVisualizations;
    }
    return tabFilteredVisualizations.filter((viz) => {
      const vizTagsArray = Array.isArray(viz.tags) ? viz.tags : [];
      return tagFilter.some((selectedTag) => vizTagsArray.includes(selectedTag));
    });
  }, [tabFilteredVisualizations, tagFilter]);

  useEffect(() => {
    setPage(1);
  }, [debouncedFilterValue]);

  const [activeScopedVisualizations, pages] = useMemo(() => {
    const offset = (page - 1) * visualizationLimit;
    const trimmedFilter = debouncedFilterValue?.trim();
    const filteredVisualizations = trimmedFilter
      ? tagFilteredVisualizations.filter((v) => {
          const filters = trimmedFilter.split(' ');
          const configDisplayName = EXTRA_SMALL_VIZ_TYPES.has(v?.type)
            ? v?.configuration?.label
            : v?.configuration?.title;
          const searchText = configDisplayName || v?.name || '';
          return filters.every((filter) => searchText.toLowerCase().includes(filter.toLowerCase()));
        })
      : tagFilteredVisualizations;

    return [
      filteredVisualizations.slice(offset, offset + visualizationLimit),
      Math.ceil(filteredVisualizations.length / visualizationLimit),
    ];
  }, [debouncedFilterValue, page, visualizationLimit, tagFilteredVisualizations]);

  useEffect(() => {
    removeLastItemBorder();
  }, [activeScopedVisualizations, isFullScreen]);

  const seriesVisualizations = useMemo(
    () => (activeScopedVisualizations || []).filter((v) => !LAST_VALUE_VISUALIZATIONS.has(v?.type)),
    [activeScopedVisualizations]
  );

  // Panel-level batching: subscribe series visualizations at once (last-value types subscribe per-visualization)
  usePanelSubscription(seriesVisualizations, panelId);

  let formattedWidth = {
    narrow: 'medium-small',
    wide: 'medium',
    widest: 'large',
  }[format];

  if (format !== 'narrow' || !['large', 'medium'].includes(width)) {
    formattedWidth = width;
  }

  const signalViewerVisualizations = scopedVisualizations.some((visualization) =>
    SELECTABLE_VIZUALISATIONS.includes(visualization?.type)
  );

  const shouldHideVisualizationToolbar =
    hideToolbar || hiddenToolbarItems.includes('signalViewerTools');

  const vizPanelClasses = useMemo(
    () =>
      classnames('visualizations-panel-component', `width-${formattedWidth}`, `height-${height}`, {
        'no-toolbar': hideToolbar && !signalViewerVisualizations,
      }),
    [formattedWidth, hideToolbar, height, signalViewerVisualizations]
  );

  const noVisualization = (text) => <div className="empty-visualization">{text}</div>;

  const handleOnClose = () => {
    setSelected(null);
    dispatch(resetVisualizations());
  };

  const onFormReset = () => {
    setSelectedVisualizations([]);
    setAllSelected(false);
    setSignalViewer(false);
    setShowExportForm(false);
    dispatch(startSitePolling());
  };

  const toggleAllSelected = () => {
    // Consider only graph, duval and star visualizations
    setSelectedVisualizations(
      allSelected
        ? []
        : activeScopedVisualizations.filter((v) => SELECTABLE_VIZUALISATIONS.includes(v.type))
    );
    setAllSelected((previous) => !previous);
  };

  const toggleSelect = (visualization) => {
    setSelectedVisualizations((viz) => {
      if (!viz.some((v) => v.id === visualization.id)) {
        return [...viz, visualization];
      }
      setAllSelected(false);
      return viz.filter((item) => item.id !== visualization.id);
    });
  };
  // Since the duval and star point don't display all signals,
  // we need to extract the signals that are actually assigned to plots
  const selectedVizVariables = () =>
    selectedVisualizations.flatMap((viz) => {
      switch (viz.type) {
        case 'duval': {
          return (viz.configuration.plots || [])
            .flatMap((plot) => [plot.leftId, plot.rightId, plot.bottomId])
            .map((signalId) => viz.variables.find((v) => v.id === signalId?.split('.')[0]))
            .filter(Boolean);
        }
        case 'duvalPentagon': {
          return (viz.configuration.plots || [])
            .flatMap((plot) => [plot.h2Id, plot.c2h6Id, plot.ch4Id, plot.c2h4Id, plot.c2h2Id])
            .map((signalId) => viz.variables.find((v) => v.id === signalId?.split('.')[0]))
            .filter(Boolean);
        }
        case 'star': {
          return getStarPlotVariables(viz);
        }
        default: {
          return viz.variables;
        }
      }
    });

  if (components.length === 0) {
    return <Loader />;
  }

  if (scopedVisualizations.length === 0) {
    return noVisualization('No visualizations available');
  }

  return (
    <>
      <VisualizationsHeader
        filterValue={filterValue}
        format={format}
        isFullScreen={isFullScreen}
        setFilterValue={setFilterValue}
        panelId={panelId}
        signalViewerVisualizations={signalViewerVisualizations}
        anySelected={selectedVisualizations.length > 0}
        allSelected={allSelected}
        toggleAllSelected={toggleAllSelected}
        openSignalViewer={() => setSignalViewer(true)}
        exportTimeSeries={() => setShowExportForm(true)}
        availableTags={tags || []}
        includeTags={[]}
        setTagFilter={setTagFilter}
        showTagFilter={!shouldShowTabs || validTabGroups?.length < 2}
        hideToolbar={hideToolbar}
        hiddenToolbarItems={hiddenToolbarItems}
      />

      {shouldShowTabs && validTabGroups?.length >= 2 && (
        <Tabs
          className="visualizations-panel-tabs"
          onTabChange={(tabId) => setActiveTab(tabId)}
          defaultSelected={0}
        >
          {validTabGroups.map((tabGroup, index) => (
            <Tab
              key={index}
              id={index}
              title={<LabelWithTooltip name={tabGroup.name} />}
            />
          ))}
        </Tabs>
      )}

      {activeScopedVisualizations.length > 0 ? (
        <>
          {signalViewer && signalViewerVisualizations && (
            <SignalViewerContainer
              selectedVisualizations={selectedVisualizations}
              pollingDateRange={pollingDateRange}
              site={site}
              onFormReset={onFormReset}
              theme={theme}
              tz={timezone}
              canDeleteTemplate={canDeleteTemplate}
            ></SignalViewerContainer>
          )}
          {showExportForm && signalViewerVisualizations && (
            <ExportTimeSeriesModal
              selectedVizVariables={selectedVizVariables()}
              onCloseModal={handleCloseExportModal}
              showAggregation={false}
              exportGranularity={exportGranularity}
              onExportCompleted={onFormReset}
              parentPanelId={panelId}
            />
          )}
          <div className={vizPanelClasses}>
            {activeScopedVisualizations.map((visualization) => {
              if (!visualization) return null;
              const onOpenSignalViewer = () => {
                setSelectedVisualizations([visualization]);
                setSignalViewer(true);
              };
              return (
                <React.Fragment key={`${visualization.id}`}>
                  <div
                    data-testid="visualization-wrapper"
                    className="visualization-wrapper"
                    onDoubleClick={() => {
                      if (canEditVisualizations) {
                        setSelected(visualization.id);
                      }
                    }}
                    style={{ marginBottom: `${visualization.variables.length * 6.5}px` }}
                  >
                    <Visualization
                      syncVisualizationTooltip={syncVisualizationTooltip}
                      panelId={panelId}
                      visualization={visualization}
                      pollingDateRange={pollingDateRange}
                      selected={selectedVisualizations.some((v) => v.id === visualization.id)}
                      onSelect={toggleSelect}
                      exportGranularity={exportGranularity}
                      hideToolbar={shouldHideVisualizationToolbar}
                      openSignalViewer={onOpenSignalViewer}
                    />
                  </div>
                </React.Fragment>
              );
            })}
          </div>
          {pages > 1 && (
            <Pagination
              pages={pages}
              page={page}
              setPage={setPage}
            />
          )}
        </>
      ) : (
        noVisualization('No visualizations found')
      )}
      {selected !== null && (
        <VisualizationEditor
          visualizationId={selected}
          visualizationNames={visualizationNames}
          onClose={() => handleOnClose()}
          parentPanelId={panelId}
          componentScope={componentScope}
        />
      )}
    </>
  );
};

export default VisualizationsPanel;
